/**
 * Role Levels (used in API response)
 */
// RoleView ma pan changes krvo jo Role Level change thai to
export const RoleLevel = {
  SUPER_ADMIN: 1,
  ORGANIZATION_ADMIN: 2,
  MANAGER: 3,
  AGENT: 4,
  VIEWER: 5,
};

//  if Permission changes at time also update RoleFormView component
export const Permission = {
  // User Management
  USERS_CREATE: "users:create",
  USERS_READ: "users:read",
  USERS_UPDATE: "users:update",
  USERS_DELETE: "users:delete",
  USERS_ASSIGN_ROLE: "users:assign_role",
  USERS_REVOKE_ROLE: "users:revoke_role",
  USERS_GRANT_TEMPORARY: "users:grant_temporary",
  USERS_REVOKE_TEMPORARY: "users:revoke_temporary",
  USERS_ALL: "users:*",

  // Lead Management
  LEADS_CREATE: "leads:create",
  LEADS_READ: "leads:read",
  LEADS_UPDATE: "leads:update",
  LEADS_DELETE: "leads:delete",
  LEADS_ASSIGN: "leads:assign",
  LEADS_IMPORT: "leads:import",
  LEADS_EXPORT: "leads:export",
  LEADS_TRANSFER: "leads:transfer",
  LEADS_ALL: "leads:*",

  LEADS_BUY: "leads:buy",
  LEADS_RENT: "leads:rent",

  // builders or developer Management
  BUILDERS_CREATE: "builders:create",
  BUILDERS_READ: "builders:read",
  BUILDERS_UPDATE: "builders:update",
  BUILDERS_DELETE: "builders:delete",
  BUILDERS_ASSIGN: "builders:assign",
  BUILDERS_ALL: "builders:*",

  // Project Management
  PROJECTS_CREATE: "projects:create",
  PROJECTS_READ: "projects:read",
  PROJECTS_UPDATE: "projects:update",
  PROJECTS_DELETE: "projects:delete",
  PROJECTS_ASSIGN: "projects:assign",
  PROJECTS_ALL: "projects:*",

  // Role & Permission Management
  ROLES_CREATE: "roles:create",
  ROLES_READ: "roles:read",
  ROLES_UPDATE: "roles:update",
  ROLES_DELETE: "roles:delete",
  ROLES_ASSIGN: "roles:assign",
  ROLES_REVOKE: "roles:revoke",
  ROLES_GRANT_TEMPORARY: "roles:grant_temporary",
  ROLES_REVOKE_TEMPORARY: "roles:revoke_temporary",
  ROLES_ALL: "roles:*",
  ROLES_ASSIGN_PERMISSION: "ROLES_ASSIGN_PERMISSION",
  ROLES_REVOKE_PERMISSION: "ROLES_REVOKE_PERMISSION",

  // Permission Management
  PERMISSIONS_CREATE: "permissions:create",
  PERMISSIONS_READ: "permissions:read",
  PERMISSIONS_UPDATE: "permissions:update",
  PERMISSIONS_DELETE: "permissions:delete",
  PERMISSIONS_GRANT: "permissions:grant",
  PERMISSIONS_REVOKE: "permissions:revoke",
  PERMISSIONS_ALL: "permissions:*",
};

/**
 * Permission string helpers for building permission checks
 * These are helper functions, not hardcoded permissions
 */
export const PermissionHelper = {
  // Check if user has any permission for a resource
  hasAnyForResource: (permissions, resource) => {
    return permissions.some(p => p.startsWith(`${resource}:`));
  },

  // Check if user has wildcard permission for a resource
  hasWildcardForResource: (permissions, resource) => {
    return permissions.includes(`${resource}:*`);
  },

  // Build permission string
  build: (resource, action) => `${resource}:${action}`,

  // Extract resource from permission string
  getResource: permission => permission.split(":")[0],

  // Extract action from permission string
  getAction: permission => permission.split(":")[1],
};

/**
 * Check if user has a specific permission
 * @param {Array} userPermissions - Array of user permissions from JWT
 * @param {String} requiredPermission - Permission to check
 * @returns {Boolean}
 */
export const hasPermission = (userPermissions = [], requiredPermission) => {
  if (!requiredPermission || !Array.isArray(userPermissions)) return false;

  // Check for exact match
  if (userPermissions.includes(requiredPermission)) return true;

  // Check for wildcard permissions (e.g., "users:*" grants all user permissions)
  const [resource] = requiredPermission.split(":");
  return userPermissions.includes(`${resource}:*`);
};

/**
 * Check if user has any of the required permissions
 * @param {Array} userPermissions - Array of user permissions
 * @param {Array} requiredPermissions - Array of permissions (OR logic)
 * @returns {Boolean}
 */
export const hasAnyPermission = (userPermissions = [], requiredPermissions = []) => {
  if (!Array.isArray(requiredPermissions) || requiredPermissions.length === 0) {
    return true;
  }

  return requiredPermissions.some(permission => hasPermission(userPermissions, permission));
};

/**
 * Check if user has all of the required permissions
 * @param {Array} userPermissions - Array of user permissions
 * @param {Array} requiredPermissions - Array of permissions (AND logic)
 * @returns {Boolean}
 */
export const hasAllPermissions = (userPermissions = [], requiredPermissions = []) => {
  if (!Array.isArray(requiredPermissions) || requiredPermissions.length === 0) {
    return true;
  }

  return requiredPermissions.every(permission => hasPermission(userPermissions, permission));
};

/**
 * Check if user's role level is sufficient
 * @param {Number} userRoleLevel - User's role level
 * @param {Number} requiredRoleLevel - Required role level
 * @returns {Boolean}
 */
export const hasRoleLevel = (userRoleLevel, requiredRoleLevel) => {
  // Lower number = higher privilege
  return userRoleLevel <= requiredRoleLevel;
};

/**
 * Get user RBAC context from login state
 * @param {Object} loginState - Redux login state
 * @returns {Object} RBAC context
 */
export const getUserRBACContext = loginState => {
  const user = loginState?.user || {};

  return {
    userId: user.id,
    permissions: user.permissions || [],
    tempPermissions: user.tempPermissions || [],
    roleLevel: user.roleLevel,
  };
};
